import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Injector,
  Input,
  forwardRef,
  inject,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  AbstractControl,
  ControlValueAccessor,
  FormGroupDirective,
  NG_VALUE_ACCESSOR,
  NgControl,
  NgForm,
  Validators,
} from "@angular/forms";

import { HttpResponse } from "@angular/common/http";
import { MasterMethodsService } from "src/app/shared/services/master-methods.service";
import { MessagesService } from "src/app/shared/services/messages.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { environment } from "src/environments/environment";
import { ApiRoutes } from "src/app/shared/app/routers/ApiRoutes";
import { IBaseResponse } from "src/app/shared/app/models/App/IBaseResponse";
import { DndDirective } from "../dnd.directive";
import { DropzoneService } from "../dropzone.service";

type FileValue = File | string | null;

@Component({
  selector: "app-file-input",
  standalone: true,
  imports: [CommonModule, DndDirective],
  templateUrl: "./file-input.component.html",
  styleUrls: ["./file-input.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileInputComponent),
      multi: true,
    },
  ],
})
export class FileInputComponent implements ControlValueAccessor {
  private masterMethod: MasterMethodsService = inject(MasterMethodsService);
  private message: MessagesService = inject(MessagesService);
  private injector: Injector = inject(Injector);
  private destroyRef = inject(DestroyRef);
  private filesService = inject(DropzoneService);
  private ngControl: NgControl | null = null;

  private formDir = inject(FormGroupDirective, { optional: true });
  private ngForm = inject(NgForm, { optional: true });
  private detect = inject(ChangeDetectorRef);
  @Input() isDropzone: boolean = false;
  @Input() isDeletable: boolean = true;

  @Input() label = "";
  @Input() accept = "";
  @Input() placeholder = "No file chosen";
  /**
   * Custom validation or error messages for the form input.
   *
   * The object keys represent validation error types (e.g., 'required', 'minlength'),
   * and the values are the corresponding custom messages to display.
   *
   * @example
   * customMessages = { required: 'This field is mandatory', minlength: 'Too short' }
   */
  @Input() customMessages: Partial<Record<string, string>> = {};

  value: FileValue = null;
  disabled = false;

  private readonly msTypes = ["excel", "word", "powerpoint"];
  private readonly msLink =
    "https://view.officeapps.live.com/op/embed.aspx?src=";
  private readonly env = environment.baseURL;

  private onChange: (val: FileValue) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    try {
      this.ngControl = this.injector.get(NgControl);
    } catch (e) {
      this.ngControl = null;
    }

    if (!this.ngControl) {
      throw new Error(
        "app-input (InputFieldComponent) requires formControlName or ngModel",
      );
    }

    this.ngControl.valueAccessor = this;

    Promise.resolve().then(() => {
      const ownControl = this.ngControl?.control;

      if (ownControl) {
        const original = ownControl.markAsTouched.bind(ownControl);
        ownControl.markAsTouched = (...args: any[]) => {
          original(...args);
          this.detect.markForCheck();
        };
      }

      if (this.formDir) {
        this.formDir.ngSubmit
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.control.markAsTouched();
            this.detect.markForCheck();
          });
      }

      if (this.ngForm) {
        this.ngForm.ngSubmit
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.control.markAsTouched();
            this.detect.markForCheck();
          });
      }
    });
  }
  get hasError(): boolean {
    return !!(this.control?.invalid && this.control?.touched);
  }

  get isRequired(): boolean {
    return this.control?.hasValidator(Validators.required) ?? false;
  }
  get control(): AbstractControl {
    return this.ngControl?.control!;
  }
  get fileName(): string {
    if (!this.value) return "";

    // Handle File object
    if (this.value instanceof File) {
      return this.value.name;
    }

    // Handle string (file path)
    if (typeof this.value === "string") {
      const path = this.value.split("?")[0].split("#")[0];
      const name = path.split(/[\\/]/).pop() || path;
      try {
        return decodeURIComponent(name);
      } catch {
        return name;
      }
    }

    return "";
  }

  get hasFile(): boolean {
    return !!this.value;
  }

  // ControlValueAccessor
  writeValue(val: FileValue): void {
    this.value = val ?? null;
    this.detect.markForCheck();
  }
  registerOnChange(fn: (val: FileValue) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.detect.markForCheck();
  }

  // Button 1: select when empty, preview when a file exists
  onSelect(native: HTMLInputElement): void {
    if (this.disabled) return;
    if (this.hasFile) {
      this.preview();
      return;
    }
    native.click();
    this.detect.markForCheck();
  }

  onFileChosen(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (file) {
      this.value = file;
      this.onChange(this.value);
      this.onTouched();
    }
    input.value = "";
  }

  preview(): void {
    if (!this.value) return;

    // local picked file
    if (this.value instanceof File) {
      const url = URL.createObjectURL(this.value);
      this.openFileWithSystem(
        url,
        this.getFileType(this.fileName),
        this.fileName,
      );
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return;
    }

    // backend file: MS-office types go through the online viewer
    if (this.msTypes.includes(this.getFileType(this.fileName))) {
      this.masterMethod
        .generateFileLink(this.value)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res: IBaseResponse<string>) => {
          if (res.status) {
            const link = `${this.msLink}${this.env}${ApiRoutes.MasterMethods.openMSFile}${res.data}`;
            window.open(link, "_blank");
          } else this.message.popup("Oops!", res.message!, "error");
        });
    } else {
      this.fetchBackend(this.value, "view");
    }
  }

  download(): void {
    if (!this.value) return;

    if (this.value instanceof File) {
      const url = URL.createObjectURL(this.value);
      this.triggerDownload(this.fileName, url);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return;
    }

    this.fetchBackend(this.value, "download");
  }

  remove(): void {
    if (!this.value) return;
    if (this.value instanceof File) {
      this.value = null;
      this.onChange(this.value);
      this.onTouched();
    } else {
      this.message
        .confirm(
          "Delete!",
          "Are you sure you want to delete it?",
          "danger",
          "question",
        )
        .then((res: any) => {
          if (res.isConfirmed) {
            this.masterMethod
              .deleteFile(this.value as string)
              .subscribe((res: HttpResponse<IBaseResponse<boolean>>) => {
                if (res.body?.status) {
                  this.value = null;
                  this.onChange(this.value);
                  this.onTouched();
                  this.message.toast("Deleted!", "info");
                } else
                  this.message.popup("Oops!", res.body?.message!, "warning");
              });
            return;
          }
        });
    }
  }
  onFileDropped(files: FileList): void {
    if (this.disabled) return;

    const file = files?.[0] ?? null;
    if (!file) return;

    if (!this.isFileAllowed(file)) {
      this.message.popup("Oops!", "File type is not allowed", "warning");
      return;
    }

    this.value = file;
    this.onChange(this.value);
    this.onTouched();
    this.detect.markForCheck();
  }

  private isFileAllowed(file: File): boolean {
    if (!this.accept) return true;

    const accepted = this.accept.split(",").map((x) => x.trim().toLowerCase());

    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    return accepted.some((type) => {
      if (type === "image/*") {
        return fileType.startsWith("image/");
      }

      if (type.startsWith(".")) {
        return fileName.endsWith(type);
      }

      return fileType === type;
    });
  }

  // --- backend fetch (mirrors FilesPreviewComponent.prepareFile) ---
  private fetchBackend(path: string, action: "view" | "download"): void {
    this.masterMethod
      .downloadFile(path)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          const fileName =
            res.headers
              .get("content-disposition")
              ?.split(";")[1]
              ?.split("=")[1]
              ?.replace(/"/g, "") || this.fileName;
          const blob = new Blob([res.body as BlobPart], {
            type: this.getMimeType(fileName),
          });
          const blobUrl = window.URL.createObjectURL(blob);

          if (action === "download") {
            this.triggerDownload(fileName, blobUrl);
          } else {
            this.openFileWithSystem(
              blobUrl,
              this.getFileType(fileName),
              fileName,
            );
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
          }
          this.detect.markForCheck();
        },
        error: (error) => this.message.popup("Oops!", error.message, "error"),
      });
  }

  private openFileWithSystem(
    blobUrl: string,
    fileType: string,
    fileName: string,
  ): void {
    switch (fileType) {
      case "image":
      case "pdf":
      case "text":
        window.open(blobUrl, "_blank");
        break;
      case "csv":
        break;
      default:
        this.triggerDownload(fileName, blobUrl);
        break;
    }
  }

  private triggerDownload(fileName: string, blobUrl: string): void {
    const a = document.createElement("a");
    a.setAttribute("style", "display:none;");
    document.body.appendChild(a);
    a.download = fileName;
    a.href = blobUrl;
    a.target = "_blank";
    a.click();
    document.body.removeChild(a);
    this.detect.markForCheck();
  }

  private getFileType(fileName: string): string {
    const ext = fileName.split(".").pop()?.toLowerCase() || "";
    const map: { [k: string]: string } = {
      xlsx: "excel",
      xls: "excel",
      doc: "word",
      docx: "word",
      pdf: "pdf",
      txt: "text",
      csv: "csv",
      ppt: "powerpoint",
      pptx: "powerpoint",
      zip: "archive",
      png: "image",
      jpg: "image",
      jpeg: "image",
      gif: "image",
    };
    this.detect.markForCheck();
    return map[ext] || "unknown";
  }

  private getMimeType(fileName: string): string {
    const ext = fileName.split(".").pop()?.toLowerCase() || "";
    const map: { [k: string]: string } = {
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      xls: "application/vnd.ms-excel",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      pdf: "application/pdf",
      txt: "text/plain",
      csv: "text/csv",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      zip: "application/zip",
      png: "image/png",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      gif: "image/gif",
    };
    return map[ext] || "application/octet-stream";
  }

  get errorMessages(): string[] {
    if (!this.control?.errors) return [];

    return Object.entries(this.control.errors).map(([key, val]) => {
      // custom override
      if (this.customMessages[key]) {
        return this.customMessages[key]!;
      }
      // default messages
      const defaults: Record<string, string> = {
        required: `Required`,
        email: `Invalid email`,
        minlength: `Must be at least ${(<any>val).requiredLength} characters`,
        maxlength: `Must be no more than ${
          (<any>val).requiredLength
        } characters`,
        pattern: `Invalid format`,
      };
      this.detect.markForCheck();
      return defaults[key] || `${this.label} is invalid`;
    });
  }
}
